// Libs
import { ModelBase } from "@shoothill/core";
import * as MobX from "mobx";

// Models
import { MoneyGifterModel } from "Globals/Models/Domain/FormSections/IncomeSavingsAndOutgoings/MoneyGifterModel";
import { OutstandingLoanModel } from "Globals/Models/Domain/FormSections/IncomeSavingsAndOutgoings/OutstandingLoanModel";
import { CreditCardModel } from "Globals/Models/Domain/FormSections/IncomeSavingsAndOutgoings/CreditCardModel";
import { FinancialCommitmentModel } from "Globals/Models/Domain/FormSections/IncomeSavingsAndOutgoings/FinancialCommitmentModel";

// Dto
import { IncomeDetailDTO } from "Globals/Models/DTO/IncomeDetailDTO";
import { MoneyGifterDTO } from "Globals/Models/DTO/MoneyGifterDTO";
import { OutstandingLoanDTO } from "Globals/Models/DTO/OutstandingLoanDTO";
import { CreditCardDTO } from "Globals/Models/DTO/CreditCardDTO";
import { FinancialCommitmentDTO } from "Globals/Models/DTO/FinancialCommitmentDTO";

export class IncomeDetailsModel extends ModelBase<IncomeDetailsModel, IncomeDetailsAndRelated> {
    @MobX.observable public id: string | null = null;
    @MobX.observable public formId: string = "";
    @MobX.observable public applicantId: string = "";
    @MobX.observable public grossAnnualIncome: number = 0;
    @MobX.observable public regularAnnualOvertimeBonusCommission: number = 0;
    @MobX.observable public hasAdditionalMonthlyHouseholdIncome: boolean = false;
    @MobX.observable public workingTaxCredit: number | null = null;
    @MobX.observable public childTaxCredit: number | null = null;
    @MobX.observable public disabilityAllowance: number | null = null;
    @MobX.observable public guaranteedMaintenanceIncome: number | null = null;
    @MobX.observable public otherIncome: number | null = null;
    @MobX.observable public totalSavings: number | null = null;
    @MobX.observable public claimsGiftedMoney: boolean = false;
    @MobX.observable public currentRent: number | null = 0;
    @MobX.observable public hasOutstandingLoans: boolean = false;
    @MobX.observable public hasCreditCards: boolean = false;
    @MobX.observable public hasOtherRegularMonthlyFinancialCommitments: boolean = false;
    @MobX.observable public moneyGifterSameForApplicantTwo: boolean = false;
    @MobX.observable public creditCardSameForApplicantTwo: boolean = false;
    @MobX.observable public financialCommitmentSameForApplicantTwo: boolean = false;
    @MobX.observable public createdDate: Date | null = null;
    @MobX.observable public isDeleted: boolean = false;

    @MobX.observable public moneyGifterModels: MoneyGifterModel[] = [];
    @MobX.observable public outstandingLoanModels: OutstandingLoanModel[] = [];
    @MobX.observable public creditCardModels: CreditCardModel[] = [];
    @MobX.observable public financialCommitmentModels: FinancialCommitmentModel[] = [];

    @MobX.action
    public fromDto = (model: IncomeDetailsAndRelated): void => {
        if (model !== null && model !== undefined) {
            this.id = model.incomeDetail.id;
            this.formId = model.incomeDetail.formId;
            this.applicantId = model.incomeDetail.applicantId;
            this.grossAnnualIncome = model.incomeDetail.grossAnnualIncome;
            this.regularAnnualOvertimeBonusCommission = model.incomeDetail.regularAnnualOvertimeBonusCommission;
            this.hasAdditionalMonthlyHouseholdIncome = model.incomeDetail.hasAdditionalMonthlyHouseholdIncome;
            this.workingTaxCredit = model.incomeDetail.workingTaxCredit;
            this.childTaxCredit = model.incomeDetail.childTaxCredit;
            this.disabilityAllowance = model.incomeDetail.disabilityAllowance;
            this.guaranteedMaintenanceIncome = model.incomeDetail.guaranteedMaintenanceIncome;
            this.otherIncome = model.incomeDetail.otherIncome;
            this.totalSavings = model.incomeDetail.totalSavings;
            this.claimsGiftedMoney = model.incomeDetail.claimsGiftedMoney;
            this.currentRent = model.incomeDetail.currentRent;
            this.hasOutstandingLoans = model.incomeDetail.hasOutstandingLoans;
            this.hasCreditCards = model.incomeDetail.hasCreditCards;
            this.hasOtherRegularMonthlyFinancialCommitments = model.incomeDetail.hasOtherRegularMonthlyFinancialCommitments;
            this.createdDate = model.incomeDetail.createdDate;
            this.isDeleted = model.incomeDetail.isDeleted;

            // Money gifters
            model.moneyGifterModels.forEach((moneyGifter) => {
                let localMoneyGifterModel: MoneyGifterModel = new MoneyGifterModel();
                localMoneyGifterModel.fromDto(moneyGifter);
                this.moneyGifterModels.push(localMoneyGifterModel);
            });

            // Load family Address
            model.outstandingLoanModels.forEach((outstandingLoan) => {
                let localOutstandingLoanModel: OutstandingLoanModel = new OutstandingLoanModel();
                localOutstandingLoanModel.fromDto(outstandingLoan);
                this.outstandingLoanModels.push(localOutstandingLoanModel);
            });

            // Credit card
            model.creditCardModels.forEach((creditCard) => {
                let localCreditCardModel: CreditCardModel = new CreditCardModel();
                localCreditCardModel.fromDto(creditCard);
                this.creditCardModels.push(localCreditCardModel);
            });

            // Credit card
            model.financialCommitmentModels.forEach((financialCommitment) => {
                let localFinancialCommitmentModel: FinancialCommitmentModel = new FinancialCommitmentModel();
                localFinancialCommitmentModel.fromDto(financialCommitment);
                this.financialCommitmentModels.push(localFinancialCommitmentModel);
            });
        }
    };

    @MobX.action
    public fromIncomeDetailDto = (model: IncomeDetailDTO): void => {
        if (model !== null && model !== undefined) {
            this.id = model.id;
            this.formId = model.formId;
            this.applicantId = model.applicantId;
            this.grossAnnualIncome = model.grossAnnualIncome;
            this.regularAnnualOvertimeBonusCommission = model.regularAnnualOvertimeBonusCommission;
            this.hasAdditionalMonthlyHouseholdIncome = model.hasAdditionalMonthlyHouseholdIncome;
            this.workingTaxCredit = model.workingTaxCredit;
            this.childTaxCredit = model.childTaxCredit;
            this.disabilityAllowance = model.disabilityAllowance;
            this.guaranteedMaintenanceIncome = model.guaranteedMaintenanceIncome;
            this.otherIncome = model.otherIncome;
            this.totalSavings = model.totalSavings;
            this.claimsGiftedMoney = model.claimsGiftedMoney;
            this.currentRent = model.currentRent;
            this.hasOutstandingLoans = model.hasOutstandingLoans;
            this.hasCreditCards = model.hasCreditCards;
            this.hasOtherRegularMonthlyFinancialCommitments = model.hasOtherRegularMonthlyFinancialCommitments;
            this.createdDate = model.createdDate;
            this.isDeleted = model.isDeleted;
        }
    };

    @MobX.action
    public toDto = (): IncomeDetailDTO => {
        let model: IncomeDetailDTO = {
            id: this.id,
            formId: this.formId,
            applicantId: this.applicantId,
            grossAnnualIncome: this.grossAnnualIncome,
            regularAnnualOvertimeBonusCommission: this.regularAnnualOvertimeBonusCommission,
            hasAdditionalMonthlyHouseholdIncome: this.hasAdditionalMonthlyHouseholdIncome,
            workingTaxCredit: this.workingTaxCredit,
            childTaxCredit: this.childTaxCredit,
            disabilityAllowance: this.disabilityAllowance,
            guaranteedMaintenanceIncome: this.guaranteedMaintenanceIncome,
            otherIncome: this.otherIncome,
            totalSavings: this.totalSavings,
            claimsGiftedMoney: this.claimsGiftedMoney,
            currentRent: this.currentRent,
            hasOutstandingLoans: this.hasOutstandingLoans,
            hasCreditCards: this.hasCreditCards,
            hasOtherRegularMonthlyFinancialCommitments: this.hasOtherRegularMonthlyFinancialCommitments,
            createdDate: this.createdDate,
            isDeleted: this.isDeleted,
        };
        return model;
    };
}

// Extend interface to accept new form sections
export interface IncomeDetailsAndRelated {
    incomeDetail: IncomeDetailDTO;
    moneyGifterModels: MoneyGifterDTO[];
    outstandingLoanModels: OutstandingLoanDTO[];
    creditCardModels: CreditCardDTO[];
    financialCommitmentModels: FinancialCommitmentDTO[];
}
